import axios from "@/api/_axios";
import { API } from "@/api/apiEndPoints";

/**
 * Position Pay Range Subscription APIs — Rule 29 (ER-10777/10778/10779/10781).
 * Wired to the real backend (ER-10780) as of ER-10782.
 */

export const positionPayRangeSubscriptionApiConfig = {
  useMock: false,
};

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function accountBase(accountId = getAccountId()) {
  return `${API.ACCOUNT_LIST_V2}${accountId}/`;
}

function delay(ms = 300) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// —— Mock data (raw API shape — same shape the real endpoint returns,
// so mapPositionSubscriptionRow.js never has to special-case mock vs real) ——

const MOCK_SUBSCRIPTION_A = {
  id: 501,
  pay_range_id: 3066,
  pay_range_code: "P-3066",
  pay_range_name: "Communications Director Pay Range",
  min_amount: 140000,
  max_amount: 180000,
  effective_from: "2026-01-01",
  effective_to: null,
  readiness: { min: 140000, at50: 153000, at100: 167000, max: 180000 },
  status: "Current",
  country_code: "us",
  currency_code: "USD",
  pay_frequency: "Annual",
  location_name: "Country Wide",
  version_number: 2,
  version_effective_from: "2026-01-01",
  version_effective_to: "2026-02-03",
};

const MOCK_SUBSCRIPTION_B = {
  id: 502,
  pay_range_id: 3066,
  pay_range_code: "P-3066",
  pay_range_name: "Communications Director Pay Range",
  min_amount: 140000,
  max_amount: 180000,
  effective_from: "2024-01-01",
  effective_to: "2026-07-15",
  readiness: { min: 140000, at50: 153000, at100: 167000, max: 180000 },
  status: "Lapsed",
  country_code: "us",
  currency_code: "USD",
  pay_frequency: "Annual",
  location_name: "Country Wide",
  version_number: 2,
  version_effective_from: "2026-01-01",
  version_effective_to: "2026-02-03",
};

// Gap position (no current subscription) but with a future-scheduled one —
// exercises the "Selected Conflict" callout in SubscribeToPayRangeDialog
// (picking a new effective date that lands inside this window).
const MOCK_FUTURE_SUBSCRIPTION_32 = {
  id: 503,
  pay_range_id: 2841,
  pay_range_code: "P-2841",
  pay_range_name: "Engineering Manager Pay Range (Legacy)",
  min_amount: 120000,
  max_amount: 160000,
  effective_from: "2027-01-01",
  effective_to: null,
  readiness: { min: 120000, at50: 133000, at100: 147000, max: 160000 },
  status: "Future",
};

const MOCK_SUBSCRIPTION_HISTORY = {
  32: [MOCK_FUTURE_SUBSCRIPTION_32],
};

const MOCK_POSITIONS = [
  {
    id: 14,
    code: "14",
    name: "Communications Director",
    department: "Marketing",
    occupant: { id: 1, first_name: "Jane", last_name: "Doe", avatar: null },
    subscription: MOCK_SUBSCRIPTION_A,
  },
  {
    id: 21,
    code: "21",
    name: "HR Manager",
    department: "Engineering",
    occupant: null,
    subscription: MOCK_SUBSCRIPTION_B,
  },
  {
    id: 32,
    code: "32",
    name: "Engineering Manager",
    department: "Engineering",
    occupant: { id: 2, first_name: "Jennifer", last_name: "Ryley", avatar: null },
    subscription: null,
  },
  {
    id: 45,
    code: "45",
    name: "Finance Director",
    department: "Finance",
    occupant: { id: 3, first_name: "Meera", last_name: "Nair", avatar: null },
    subscription: MOCK_SUBSCRIPTION_B,
  },
];

function mockFilterAndPaginate(params) {
  const search = String(params.name || "")
    .trim()
    .toLowerCase();
  const filtered = search
    ? MOCK_POSITIONS.filter(
        (p) => p.name.toLowerCase().includes(search) || p.code.toLowerCase().includes(search)
      )
    : MOCK_POSITIONS;

  const page = params.page || 1;
  const pageSize = params.pageSize || 25;
  const start = (page - 1) * pageSize;
  const results = filtered.slice(start, start + pageSize);

  return {
    count: filtered.length,
    next: start + pageSize < filtered.length ? "mock-next" : null,
    previous: page > 1 ? "mock-previous" : null,
    results,
  };
}

/**
 * List positions with their current pay range subscription as of a date (Rule 10).
 * Server-side name search is supported — matches both position name and code
 * (case-insensitive, multi-term AND; added in ER-11138).
 * @param {{ asOf?: string, status?: string[], page?: number, pageSize?: number, name?: string }} [params]
 */
export async function listPositionPayRangeSubscriptions(params = {}) {
  if (positionPayRangeSubscriptionApiConfig.useMock) {
    await delay();
    return { data: mockFilterAndPaginate(params) };
  }

  const accountId = getAccountId();
  const query = new URLSearchParams();
  query.set("page", String(params.page || 1));
  query.set("page_size", String(params.pageSize || 25));
  if (params.asOf) query.set("as_of", params.asOf);
  if (params.name) query.set("name", params.name);
  (params.status || []).forEach((s) => query.append("status", s));

  return axios()({
    method: "get",
    url: `${accountBase(accountId)}${
      API.POSITIONS_WITH_PAY_RANGE_SUBSCRIPTIONS
    }?${query.toString()}`,
  });
}

/**
 * Subscribe a position to a PUBLISHED pay range (Rule 29).
 * @param {string|number} positionId
 * @param {{ payRangeId: string|number, effectiveFrom: string }} body
 */
export async function subscribePositionToPayRange(positionId, { payRangeId, effectiveFrom }) {
  if (positionPayRangeSubscriptionApiConfig.useMock) {
    await delay();
    const position = MOCK_POSITIONS.find((p) => String(p.id) === String(positionId));
    const subscription = {
      id: Date.now(),
      pay_range_id: payRangeId,
      pay_range_code: "P-MOCK",
      pay_range_name: "Mock Pay Range",
      min_amount: 100000,
      max_amount: 140000,
      effective_from: effectiveFrom,
      effective_to: null,
      readiness: { min: 100000, at50: 113000, at100: 127000, max: 140000 },
      status: "Current",
    };
    if (position) position.subscription = subscription;
    return { data: subscription };
  }

  const accountId = getAccountId();
  return axios()({
    method: "post",
    url: `${accountBase(accountId)}position/${positionId}/${API.PAY_RANGE_SUBSCRIPTION_SUBSCRIBE}`,
    data: {
      pay_range_id: payRangeId,
      effective_from: effectiveFrom,
    },
  });
}

/**
 * List a position's non-superseded subscriptions (current + future-scheduled).
 * Used by the Subscribe dialog to resolve the "Selected Conflict" callout —
 * the row's as-of-today subscription alone can't catch a conflict with a
 * future-scheduled one, since the "+ Subscribe" button only shows for gap
 * positions (no *current* subscription), where the row's own subscription is
 * always null.
 * @param {string|number} positionId
 */
export async function listSubscriptionsForPosition(positionId) {
  if (positionPayRangeSubscriptionApiConfig.useMock) {
    await delay();
    const position = MOCK_POSITIONS.find((p) => String(p.id) === String(positionId));
    const scheduled = MOCK_SUBSCRIPTION_HISTORY[Number(positionId)] || [];
    const current = position?.subscription ? [position.subscription] : [];
    return { data: [...current, ...scheduled] };
  }

  const accountId = getAccountId();
  return axios()({
    method: "get",
    url: `${accountBase(accountId)}position/${positionId}/${API.PAY_RANGE_SUBSCRIPTION}`,
  });
}

/**
 * End a subscription without replacement (creates a gap, Rule 5).
 * @param {string|number} positionId
 * @param {string|number} subscriptionId
 * @param {{ effectiveTo: string }} body
 */
export async function endPositionPayRangeSubscription(positionId, subscriptionId, { effectiveTo }) {
  if (positionPayRangeSubscriptionApiConfig.useMock) {
    await delay();
    const position = MOCK_POSITIONS.find((p) => String(p.id) === String(positionId));
    if (position?.subscription) {
      position.subscription = { ...position.subscription, effective_to: effectiveTo };
    }
    return { data: position?.subscription || null };
  }

  const accountId = getAccountId();
  return axios()({
    method: "post",
    url: `${accountBase(accountId)}position/${positionId}/${
      API.PAY_RANGE_SUBSCRIPTION
    }${subscriptionId}/${API.PAY_RANGE_SUBSCRIPTION_END}`,
    data: { effective_to: effectiveTo },
  });
}
